import React, { Fragment } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";

import colors from "core/colors";

const createHelpers = (width, height, css) => {
    // somehow sizes is ending up in markup, even if it is not a valid svg attribute
    // until we have a better solution, just render it empty, instead to '[Object object]'
    const sanitizeSizes = sizes =>
        Object.defineProperty(sizes, "toString", {
            value: () => "",
            enumerable: false
        });

    const getDimensions = (size, sizes) => {
        if (
            size &&
            typeof size.width === "number" &&
            typeof size.height === "number"
        ) {
            return size;
        }

        return size && sizes[size] ? sizes[size] : { width, height };
    };

    const getCss = (size, sizes, fillColor, fillColorRule, noStyles) => {
        if (noStyles) {
            return "";
        }

        const dimensions = getDimensions(size, sizes);
        const fillRule =
            fillColor && fillColorRule
                ? `${fillColorRule}{ fill: ${fillColor}; }`
                : "";
        return css`
            width: ${dimensions.width}px;
            height: ${dimensions.height}px;
            ${fillRule}
        `;
    };

    const propsToCss = ({ size, sizes, fillColor, fillColorRule, noStyles }) =>
        getCss(size, sizes, fillColor, fillColorRule, noStyles);

    return {
        getCss,
        getDimensions,
        propsToCss,
        sanitizeSizes
    };
};

const width = "24";
const height = "24";
const viewBox = "0.46 2.55 43.07 39.14";

const { getDimensions, getCss, propsToCss, sanitizeSizes } = createHelpers(
    width,
    height,
    css
);

const sizes = sanitizeSizes({
    xs: { width: 14, height: 14 },
    sm: { width: 16, height: 16 },
    md: { width: 20, height: 20 },
    lg: { width: 24, height: 24 },
    xl: { width: 40, height: 40 }
});

const Image = styled.svg`
    ${propsToCss}
`;

const children = (
    <Fragment>
        <path
            d="M23.3422 20.9126L22.8086 20.3789L21.8701 21.3174L20.9501 20.3789L20.4164 20.9126L21.3365 21.851L20.4164 22.7711L20.9501 23.2955L21.8701 22.3571L22.8086 23.2955L23.3422 22.7711L22.3946 21.851L23.3422 20.9126Z"
            fill={colors.secondary.orca}
        />
        <path
            d="M17.8863 23.2954C18.6841 23.2954 19.3308 22.6487 19.3308 21.8509C19.3308 21.0531 18.6841 20.4064 17.8863 20.4064C17.0886 20.4064 16.4418 21.0531 16.4418 21.8509C16.4418 22.6487 17.0886 23.2954 17.8863 23.2954Z"
            fill={colors.secondary.orca}
        />
        <path
            d="M24.4003 20.3789H27.3261V23.3047H24.4003V20.3789Z"
            fill={colors.secondary.orca}
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M23.1576 6.66414C24.0858 6.45353 24.7787 5.62347 24.7787 4.63157C24.7787 3.4805 23.8455 2.54736 22.6945 2.54736C21.5434 2.54736 20.6103 3.4805 20.6103 4.63157C20.6103 5.53905 21.1902 6.31107 21.9997 6.59719V12.5053H11.8103C11.4266 12.5053 11.1155 12.8163 11.1155 13.2V20.8421H4.57967C4.36905 19.9139 3.53899 19.221 2.5471 19.221C1.39602 19.221 0.462891 20.1542 0.462891 21.3053C0.462891 22.4563 1.39602 23.3895 2.5471 23.3895C3.45458 23.3895 4.2266 22.8095 4.51272 22H11.1155V30.5684C11.1155 30.9521 11.4266 31.2632 11.8103 31.2632H21.9997V37.6344C21.1902 37.9205 20.6103 38.6925 20.6103 39.6C20.6103 40.7511 21.5434 41.6842 22.6945 41.6842C23.8455 41.6842 24.7787 40.7511 24.7787 39.6C24.7787 38.6081 24.0858 37.778 23.1576 37.5674V31.2632H32.1892C32.5729 31.2632 32.8839 30.9521 32.8839 30.5684V22H39.4867C39.7729 22.8095 40.5449 23.3895 41.4524 23.3895C42.6034 23.3895 43.5366 22.4563 43.5366 21.3053C43.5366 20.1542 42.6034 19.221 41.4524 19.221C40.4605 19.221 39.6304 19.9139 39.4198 20.8421H32.8839V13.2C32.8839 12.8163 32.5729 12.5053 32.1892 12.5053H23.1576V6.66414ZM12.2734 30.1053V13.6632H31.726V30.1053H12.2734Z"
            fill={colors.secondary.orca}
        />
    </Fragment>
);

const defaultProps = {
    children,
    viewBox,
    fillColor: null,
    fillColorRule: "&&& path, &&& use, &&& g, &&& circle",
    sizes,
    size: null
};

Image.propTypes /* remove-proptypes */ = {
    fillColor: PropTypes.string,
    fillColorRule: PropTypes.string,
    viewBox: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            height: PropTypes.number.isRequired,
            width: PropTypes.number.isRequired
        })
    ]),
    sizes: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
    })
};

export default Object.assign(Image, {
    getDimensions,
    getCss,
    defaultProps,
    displayName: "Eyedropper"
});
